/**
 * <axiom-marquee> — infinite marquee whose speed reacts to page scroll.
 * Ported from Radian's section-marquee (parallax scroll behaviour).
 *
 * Markup: <axiom-marquee data-speed="40" data-direction="left" data-scroll-boost="true">
 *           <div class="marquee__track"><div class="marquee__group">…</div></div>
 *         </axiom-marquee>
 */
(function () {
  'use strict';

  if (customElements.get('axiom-marquee')) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AxiomMarquee extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.marquee__track');
      this.group = this.querySelector('.marquee__group');
      if (!this.track || !this.group || reducedMotion.matches) return;

      this.speed = parseFloat(this.dataset.speed) || 40;
      this.direction = this.dataset.direction === 'right' ? 1 : -1;
      this.scrollBoost = this.dataset.scrollBoost === 'true';
      this.pauseOnHover = this.dataset.pauseHover === 'true';
      this.offset = 0;
      this.velocity = 0;
      this.lastScroll = window.scrollY;
      this.lastTime = null;
      this.visible = false;
      this.hovered = false;

      this.fill();
      this.resizeObserver = new ResizeObserver(() => this.fill());
      this.resizeObserver.observe(this);

      this.intersection = new IntersectionObserver((entries) => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.start();
      });
      this.intersection.observe(this);

      if (this.pauseOnHover) {
        this.addEventListener('pointerenter', () => (this.hovered = true));
        this.addEventListener('pointerleave', () => (this.hovered = false));
      }

      this.onScroll = () => {
        const current = window.scrollY;
        this.velocity += (current - this.lastScroll) * 0.35;
        this.lastScroll = current;
      };
      if (this.scrollBoost) window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.intersection?.disconnect();
      window.removeEventListener('scroll', this.onScroll);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    fill() {
      this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
      // Items inside a moving track are revealed with the marquee itself; clones
      // are created after the motion controller ran, so they must never wait on it.
      this.group.querySelectorAll('[data-reveal]').forEach((el) => el.classList.add('is-inview', 'reveal-done'));
      this.groupWidth = this.group.getBoundingClientRect().width;
      if (!this.groupWidth) return;
      const copies = Math.max(1, Math.ceil((this.offsetWidth * 2) / this.groupWidth));
      for (let i = 0; i < copies; i++) {
        const clone = this.group.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-marquee-clone', '');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        this.track.appendChild(clone);
      }
    }

    start() {
      if (this.frame) return;
      this.lastTime = null;
      this.frame = requestAnimationFrame((time) => this.tick(time));
    }

    tick(time) {
      this.frame = null;
      if (!this.visible || !this.groupWidth) return;
      const delta = this.lastTime === null ? 16 : Math.min(time - this.lastTime, 64);
      this.lastTime = time;

      const base = this.hovered ? 0 : this.speed;
      this.velocity *= 0.9;
      const boost = this.scrollBoost ? Math.abs(this.velocity) : 0;
      const direction = this.scrollBoost && this.velocity < -0.5 ? -this.direction : this.direction;
      this.offset += (direction * (base + boost * 6) * delta) / 1000;

      if (this.offset <= -this.groupWidth) this.offset += this.groupWidth;
      if (this.offset > 0) this.offset -= this.groupWidth;

      this.track.style.transform = 'translate3d(' + this.offset.toFixed(2) + 'px,0,0)';
      this.frame = requestAnimationFrame((next) => this.tick(next));
    }
  }

  customElements.define('axiom-marquee', AxiomMarquee);
})();
